import { css } from '@emotion/css';
import React, { Fragment, useState, useEffect } from 'react';
import { handleEnterKeyPress } from 'utility/keyEvent.utility';
import { ShapesInput } from './componentLib/ShapesInput';
import { useTranslation } from './i18n';
import ShapeType from './ShapeType';
import { Spinner } from './componentLib/Spinner';

import { Shape } from './shapeData/types.shapes';
import { Theme, useTheme } from './componentLib/ShapesTheme';
import { ReactComponent as EmptySearchIcon } from './icons/emptySearchIcon.svg';
import { getShapesAssetList } from '../../services/shapesAsset.service';

const getShapeListStyle = css`
    width: 100%;
    display: flex;
    overflow-x: auto;
    flex-wrap: wrap;
    margin-bottom: 10px;
`;

const getShapeTypeTagStyle = css`
    margin: 0 0 4px 4px;
    color: #697582;
    font-size: 14px;
`;

const getSearchInputStyle = css`
    margin: 0 4px 15px 4px;
`;

const getCancelButtonStyle = (theme: Theme, searchValue: string) => css`
        border: none;
        background-color: transparent;
        &:focus {
            outline: none;
        }
        &:hover {
            cursor: pointer;
        }
        color: ${theme.primaryColor};
        display: ${searchValue !== '' ? 'block' : 'none'};
    `;
const getSpinnerDivStyle = (isFetching: boolean) => css`
        height: ${isFetching ? '' : 'inherit'};
        padding-top: ${isFetching ? '10%' : ''};
    `;

interface ShapeTypeListProps {
    shapeList: { collectionName: string; items: Shape[] }[];
    isDraggable: boolean;
    onSelect(shape: Shape): void;
    onDrag?(shape: Shape): void;
    isFetchingShapes: boolean;
}

export default function ShapeTypeList({ shapeList, isFetchingShapes, onSelect, onDrag, isDraggable }: ShapeTypeListProps): JSX.Element {
    const { t } = useTranslation();
    const theme = useTheme();
    const [isFetchingSearch, setIsFetchingSearch] = useState<boolean>(false);
    const [currentShapes, setCurrentShapes] = useState(shapeList);
    const [searchValue, setSearchValue] = useState<string>('');

    useEffect(() => {
        setCurrentShapes(shapeList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shapeList.length]);

    const onChangeSearchValue = (value: string) => {
        setSearchValue(value);
        if (value === '') {
            setCurrentShapes(shapeList);
        }
    };

    const onSearch = async () => {
        setIsFetchingSearch(true);
        const collections = await getShapesAssetList([searchValue.toLowerCase()]);
        if (collections.length) {
            setCurrentShapes(collections);
        } else {
            setCurrentShapes([]);
        }
        setIsFetchingSearch(false);
    };

    const onClickCancel = () => {
        setSearchValue('');
        setCurrentShapes(shapeList);
    };
    const isFetching = isFetchingShapes || isFetchingSearch;
    return (
        // eslint-disable-next-line react/jsx-fragments
        <Fragment>
            <div className={`shape-type-search ${getSearchInputStyle}`}>
                <ShapesInput
                    suffixLabel={(
                        <button type='button' className={getCancelButtonStyle(theme, searchValue)} disabled={isFetchingSearch} onClick={onClickCancel}>
                            X
                        </button>
                    )}
                    value={searchValue}
                    onChange={onChangeSearchValue}
                    onKeyPress={handleEnterKeyPress(onSearch)}
                    placeholder={t('searchPlaceholder')}
                />
            </div>
            {!isFetching && currentShapes.length === 0 ? (
                <EmptySearchIcon />
            ) : (
                <div className='shape-type-list'>
                    {!isFetchingSearch
                        && currentShapes
                        && currentShapes.map((currentItem) => (
                            <div key={`${currentItem.collectionName}`}>
                                <div className={getShapeTypeTagStyle}>{currentItem.collectionName}</div>
                                <div className={getShapeListStyle}>
                                    {currentItem.items.map((shape) => (
                                        <ShapeType
                                            key={`${shape.id}`}
                                            shape={shape}
                                            isDraggable={isDraggable}
                                            onSelect={onSelect}
                                            onDrag={onDrag}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    {isFetching && (
                        <div className={getSpinnerDivStyle(isFetchingShapes)}>
                            <Spinner size='58px' thickness={6} />{' '}
                        </div>
                    )}
                </div>
            )}
        </Fragment>
    );
}
